import {CircularProgress} from '@mui/material';
import Grid from '@mui/material/Grid';
import React, {FunctionComponent, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Button, Chip, Typography, WhiteCard} from '../../components/StyledComponents';
import {IUser} from '../../models/models';
import {useAppDispatch, useAppSelector} from '../../store/hooks';
import {
  chatgptActualPlan,
  chatgptTrialHoursLeft,
  claudeActualPlan,
  claudeTrialHoursLeft,
  getUserSelector,
  githubActualPlan,
  githubTrialHoursLeft,
  gitlabActualPlan,
  gitlabTrialHoursLeft, isHasProPlan,
  isNotionNeedsReauthSelector,
  isSubUser,
  jiraActualPlan,
  jiraTrialHoursLeft,
  loadingUser,
  notionActualPlan,
  notionTrialHoursLeft,
  roleByApp,
  shopifyActualPlan,
  shopifySubscription,
  shopifyTrialData,
  shopifyTrialHoursLeft,
  trelloActualPlan,
  trelloTrialHoursLeft
} from '../../store/selectors';
import withRedirectAfterLogout from '../../utils/withRedirectAfterLogout';
import './style.scss';
import {getDashboardActivity, getDashboardData} from "../../store/actions";
import Platform from "../../models/platforms-data";
import {
  EPlatformsNames,
  errorSubscriptionStatuses,
  EStripeSubStatus,
  inactiveSubscriptionStatuses,
  unpaidSubscriptionStatuses
} from "../../models/consts";
import {useNavigate, useSearchParams} from "react-router";
import {BadgesIFrameMode, BadgesNotionUpd, BadgesSubscription, BadgesTrial2} from "../../components/Badges/Badges";
import {IError} from "../../models/inner-models";
import Swal from "sweetalert2";
import {decoratePopUpMessage} from "../../utils/popUpTextDecorator";
import {errorBigIcon, errorIcon, iconSuccessGreen, successBigIcon, successIcon} from "../../assets/icons/icons";
import {capitalizeFirstLetterWithReplace, deleteCookie, getCookie, iniFrame} from "../../serviceWorker";
import {useWebSocket} from "../../contexts/WebSocketContext";
import {EWebsocketMethods} from "../../constants";
import DashInfoCard from "./DashInfoCards";
import {DataGrid, GridCellParams, GridColDef} from "@mui/x-data-grid";
import {IPlatformSubscriptionRow} from "../UserPages/Subscription/Subscription";
import {AddSquare, ArrowDown2, Card, Clock, Play, Code} from "iconsax-reactjs";
import withReactContent from "sweetalert2-react-content";

type ActivityServerTypes = {
  name: string,
  type: string,
  date: number,
  status: string,
  platform: string,
};

type ActivityRowTypes = ActivityServerTypes & {
  id: number;
};

const DashboardPage: FunctionComponent = () => {
  const {t: translate} = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user: IUser | null = useAppSelector(getUserSelector);
  const userLoading = useAppSelector(loadingUser);
  const isUserSub = useAppSelector(isSubUser);
  const roles = useAppSelector(roleByApp);
  const needReAuthNotion = useAppSelector(isNotionNeedsReauthSelector);
  const [searchParams] = useSearchParams();
  const extensionInstalled = searchParams.get('blextension') || getCookie('extensionInstalled');
  const hasProPlan = useAppSelector(isHasProPlan);

  const platformList: Array<string> = ['github', 'trello', 'gitlab', 'jira', 'notion', 'shopify', 'chatgpt', 'claude'];

  //Object.values(subscriptionPlans).every(Boolean) ensures all required subscriptions exist.
  const subscriptionPlans = {
    github: useAppSelector(githubActualPlan),
    trello: useAppSelector(trelloActualPlan),
    gitlab: useAppSelector(gitlabActualPlan),
    jira: useAppSelector(jiraActualPlan),
    notion: useAppSelector(notionActualPlan),
    shopify: useAppSelector(shopifyActualPlan),
    chatgpt: useAppSelector(chatgptActualPlan),
    claude: useAppSelector(claudeActualPlan),
  };

  const trialHoursLeft = {
    github: useAppSelector(githubTrialHoursLeft),
    trello: useAppSelector(trelloTrialHoursLeft),
    gitlab: useAppSelector(gitlabTrialHoursLeft),
    jira: useAppSelector(jiraTrialHoursLeft),
    notion: useAppSelector(notionTrialHoursLeft),
    shopify: useAppSelector(shopifyTrialHoursLeft),
    chatgpt: useAppSelector(chatgptTrialHoursLeft),
    claude: useAppSelector(claudeTrialHoursLeft),
  };
  const [activityRows, setActivityRows] = useState<Array<ActivityRowTypes>>();

  const [lastBackup, setLastBackup] = useState<string>('');
  const [appsCount, setAppsCount] = useState<number>(0);
  const [itemsAmount, setItemsAmount] = useState<Record<string, number>>();
  const [loadingData, setLoadingData] = useState<boolean>(true);
  const [loadingActivity, setLoadingActivity] = useState<boolean>(true);
  const {onMessage} = useWebSocket();

  const totalAddedItems = Object.values(subscriptionPlans)
    .reduce((sum, plan) => sum + (plan?.addedItemsQuantity || 0), 0);

  const activeTrials = platformList.filter(
    (item) => trialHoursLeft[item] && subscriptionPlans[item]?.status === 'active'
  );

  const activePaid = platformList.filter(
    (item) => !trialHoursLeft[item] && subscriptionPlans[item]?.status === 'active'
  );

  //redirect if no license (new account)
  useEffect(() => {
    if (user && !user.hadEverAnySubscriptions && !userLoading) {
      if (iniFrame()) {
        navigate('app-connector?jiraInIframe=true')
      } else {
        navigate('app-connector')
      }
    }
  }, [user, userLoading])

  const transformItemsCount = (backupCountRaw) => {
    const backupCount = {}
    Object.keys(backupCountRaw).forEach((key) => {
      const item = backupCountRaw[key];
      if (['github', 'gitlab'].includes(key)) {
        backupCount[key] = {
          main: {
            total: item?.repos?.allItems || item?.projects?.allItems || 0,
            backup: item?.repos?.backupedItems || item?.projects?.backupedItems || 0
          },
          sub: {
            total: item?.gists?.allItems || item?.snippets?.allItems || 0,
            backup: item?.gists?.backupedItems || item?.snippets?.backupedItems || 0
          },
        };
      } else {
        backupCount[key] = {
          main: {
            total: item?.allItems || 0,
            backup: item?.backupedItems || 0
          },
        }
      }
    });
    setItemsAmount(backupCount)
  }

  useEffect(() => {
    if (extensionInstalled) {
      deleteCookie('extensionInstalled');
      const MySwal = withReactContent(Swal);
      MySwal.fire({
        customClass: {
          popup: 'extension-installed-modal',
          denyButton: 'extension-installed-modal-deny-btn'
        },
        title: 'You’ve successfully signed in to our extension 🎉',
        text: 'To get started and start your Trial, simply back up any conversation in ChatGPT or Claude.',
        iconHtml: <img src={successBigIcon}/>,
        confirmButtonText: 'ChatGPT',
        denyButtonText: 'Claude',
        showDenyButton: true,
        denyButtonColor: 'linear-gradient(90deg, #00BBFF 0%, #3B82F6 100%)!important',
        showCloseButton: true,
      })
        .then(res => {
          if (res.isConfirmed) {
            window.open('https://chat.openai.com/chat', '_blank')
          } else if (res.isDenied) {
            window.open('https://claude.ai/', '_blank')
          }
          navigate(window.location.pathname, {replace: true})
        })
    }

    const getDashData = async () => {
      setLoadingData(true);
      try {
        const dashData = await dispatch(getDashboardData()).unwrap();
        let platformActivity: string[] = []

        if (dashData) {
          if (dashData.platforms_items_count) {
            transformItemsCount(dashData.platforms_items_count)
            setLoadingData(false)
            platformActivity = Object.keys(dashData.platforms_items_count)
          } else {
            setItemsAmount({})
          }

          if (!!platformActivity.length) {
            setAppsCount(platformActivity.length)
            getDashboardActivityScope(platformActivity)
          } else {
            setLoadingActivity(false)
          }
        }
      } catch (err) {
        const error = err as IError;
        Swal.fire({
          title: translate('notifications.titles.error'),
          text: decoratePopUpMessage(error.message || error.error as string),
          imageUrl: errorBigIcon,
        });
        console.log(err);
      } finally {
        setLoadingData(false)
      }
    }

    onMessage((data) => {
      if (data.method === EWebsocketMethods.updateDashboardPlatformItemsAmount) {
        transformItemsCount(data.platforms_items_count)
      }
    });

    getDashData()
  }, [])

  const getDashboardActivityScope = async (platformList) => {
    try {
      const activityData = await Promise.all(
        platformList.map(async (platform) => {
          const data: ActivityServerTypes[] = await dispatch(getDashboardActivity(platform)).unwrap();
          return {[platform]: data.map((item, index) => ({...item, id: index}))};
        })
      );
      const dashboardActivity: ActivityRowTypes[] = Object.assign({}, ...activityData);
      setActivityRows(dashboardActivity)
      const allDates = Object.values(dashboardActivity)
        .flat()
        .map(entry => entry.date);
      const latestDate = allDates.length > 0 ? Math.max(...allDates) : null;
      if (latestDate) {
        setLastBackup(new Date(latestDate).toLocaleString())
      }
    } catch (err) {
      const error = err as IError;
      Swal.fire({
        title: translate('notifications.titles.error'),
        text: decoratePopUpMessage(error.message || error.error as string),
        imageUrl: errorBigIcon,
      });
      console.log(err);
    } finally {
      setLoadingActivity(false)
    }
  }
  const columns: GridColDef<IPlatformSubscriptionRow>[] = [
    {
      field: 'platformName',
      headerName: 'App',
      width: 120,
      editable: false,
      sortable: false,
      headerClassName: 'subscription-table--header',
      renderCell: (params: GridCellParams) => (
        <div className='subscriptions-logo'>
          <img src={Platform[params.value as EPlatformsNames]?.smallLogo} className="platformLogo" loading="lazy"/>
          {Platform[params.value as EPlatformsNames]?.title}
        </div>
      ),
    },
    {
      field: 'status',
      headerName: translate('subscriptions.subscriptions_list.status'),
      flex: 1,
      minWidth: 110,
      editable: false,
      sortable: false,
      headerClassName: 'subscription-table--header',
      cellClassName: 'subscription-table--cell',
      renderCell: (params: GridCellParams) => {
        let subscriptionStatusClass = 'active';
        let subscriptionStatus = translate('subscriptions.statuses.active');

        if (params.row.isConnect && (unpaidSubscriptionStatuses.includes(params.value as EStripeSubStatus) || params.row.plan === 'Trial' && params.row.status === 'active')) {
          subscriptionStatusClass = 'unpaid';
          subscriptionStatus = params.row.plan === 'Trial' ? 'Trial period' : params.value.replaceAll('_', ' ');
        } else if (!params.row.isConnect || params.row.plan === 'Trial' && params.row.status === 'not started' || errorSubscriptionStatuses.includes(params.value as EStripeSubStatus)) {
          subscriptionStatusClass = 'error';
          subscriptionStatus = params.value.replaceAll('_', ' ');
          if (params.value === 'canceled after sync') subscriptionStatus = 'canceled'
          if (!params.row.isConnect || params.row.status === 'not started' && params.row.plan === 'Trial') {
            subscriptionStatusClass = 'warning'
            subscriptionStatus = 'Not connected'
          }
        } else if (inactiveSubscriptionStatuses.includes(params.value as EStripeSubStatus)) {
          subscriptionStatusClass = 'inactive';
          subscriptionStatus = params.value.replaceAll('_', ' ');
        }

        return (
          <div className='status'>
            <Chip label={subscriptionStatus} className={subscriptionStatusClass}/>
          </div>
        );
      },
    },
    {
      field: 'plan',
      headerName: translate('subscriptions.subscriptions_list.planName'),
      width: 100,
      editable: false,
      sortable: false,
      headerClassName: 'subscription-table--header',
      cellClassName: 'plan',
      renderCell: (params: GridCellParams) => {
        return !params.value || params.value === '-' ? 'N/A' : params.value === 'Trial' ? 'Free Trial' : params.value
      }
    },
    {
      field: 'planQuantity',
      headerName: 'Limits',
      flex: 1,
      editable: false,
      sortable: false,
      headerClassName: 'subscription-table--header',
      cellClassName: 'plan-quantity',
      renderCell: (params: GridCellParams) => {
        const platformName = params.row.platformName
        const diff = params.value - params.row.addedItemsQuantity
        return platformName === 'chatgpt' || platformName === 'claude' ? 'Unlimited' :
          platformName === 'jira' && params.value === 2002 ? 'Unlimited' :
            diff >= 0 ? `${diff}/${params.value}` : `0/${params.value}`
      }
    },
    {
      field: '',
      headerName: 'Actions',
      width: 190,
      editable: false,
      sortable: false,
      headerClassName: 'subscription-table--header',
      cellClassName: 'action-cell',
      renderCell: (params: GridCellParams) => {
        return (
          <div className="last-column">
            <Button
              color="primary"
              variant={"outlined"}
              onClick={() => {
                const platformName = params.row.platformName
                if (params.row.isConnect) {
                  if (params.row.status === 'active') navigate(`/platform/dashboard/${platformName}`)
                  else {
                    if (localStorage['checkoutContact']) {
                      localStorage.removeItem('checkoutContact')
                    }
                    localStorage.setItem('checkout-platform', platformName)
                    platformName === 'shopify' ? navigate('/platform/dashboard/shopify-prices-page') : navigate('/checkout');
                  }
                } else {
                  navigate(`/app-connector?platform=${platformName}&step=2`)
                }
              }}
              disabled={
                params.row.plan !== 'Trial' && params.row.isConnect && params.row.status === 'active' &&
                (((params.row.platformName === 'jira' || params.row.platformName === 'shopify') && params.row.planQuantity > 2000) || ((params.row.platformName === 'trello' || params.row.platformName === 'notion') && params.row.planQuantity > 200) ||
                  ((params.row.platformName === 'github' || params.row.platformName === 'gitlab') && params.row.planQuantity > 1000))
              }
            >
              {params.row.isConnect ?
                (params.row.status === 'active' ?
                  <Play size={16} style={{marginRight: '4px'}}/> : <Card size={16} style={{marginRight: '4px'}}/>) :
                <Code size={16} style={{marginRight: '4px'}}/>
              }
              {params.row.isConnect ?
                (params.row.status === 'active' ?
                  translate('views.platform_pages.backupNow') : translate('subscriptions.buy_subscription')) :
                (params.row.status !== 'not started' ?
                  translate('subscriptions.connect_now') : translate('subscriptions.start_trial'))
              }
            </Button>
          </div>
        )
      },
    }
  ];

  return (
    <div id="dashboard-page">
      {(loadingData || userLoading) ?
        <div className="spinner-wrapper">
          <CircularProgress/>
        </div> :
        <div className="header-block">
          {iniFrame() &&
            <BadgesIFrameMode/>
          }
          {!isUserSub && subscriptionPlans['notion']?.addedItemsQuantity === 1 && subscriptionPlans['notion']?.status === 'active' && (
            <BadgesNotionUpd platformName='notion' needAuth={needReAuthNotion}/>
          )}


          <Typography variant="h2" mb='32px' className='with-control'>
            {translate('common.default_pages.dashboard')}
          </Typography>

          <DashInfoCard lastBackup={lastBackup} total={totalAddedItems} appsCount={appsCount}/>
          <Grid container spacing={6} mt={6}>
            <Grid size={{xs: 12, sm: 12, md: 12, lg: 8, xl: 9}}>
              {activeTrials?.map(item => {
                const days = Number(trialHoursLeft[item]) || 0;
                if (!isUserSub || (isUserSub && roles?.[item])) {
                  return <BadgesTrial2 key={item} trialDays={days} platformName={item}/>;
                }
              })
              }
              {Object.values(subscriptionPlans).every(Boolean) &&
                activePaid.map((item) => {
                  const subInfo = subscriptionPlans[item];
                  const show = subInfo?.isCancel && subInfo?.finishDateInMillisecond && ((subInfo.finishDateInMillisecond - Date.now()) / (24 * 60 * 60 * 1000)) < 1;
                  if (show) {
                    return (
                      <BadgesSubscription key={item} platformName={item} isDash={true}/>
                    )
                  }
                })
              }
              {!isUserSub && subscriptionPlans && !!Object.values(subscriptionPlans).length && user && user.hadEverAnySubscriptions && (
                <WhiteCard>
                  <Typography variant="h2" display="block" className='with-control' mb={6}>
                    Apps overview
                    {!isUserSub && (
                      <Button mr={2} variant="contained" color="primary"
                              onClick={() => navigate('app-connector')}
                      >
                        <AddSquare size={16} style={{marginRight: '8px'}}/>
                        {translate('common.buttons.addPlatform')}
                      </Button>
                    )}
                  </Typography>
                  <div className="subscriptions-block">
                    <DataGrid
                      disableColumnMenu
                      disableColumnFilter
                      rows={Object.values(subscriptionPlans)?.filter(item => item.status !== 'not started') || []}
                      disableSelectionOnClick
                      rowHeight={60}
                      hideFooter
                      autoHeight
                      columns={columns}
                      disableColumnSelector
                    />
                  </div>
                </WhiteCard>
              )}
            </Grid>
            <Grid size={{xs: 12, sm: 12, md: 12, lg: 4, xl: 3}} className="activity-block">
              <WhiteCard>
                <div className="activity-block-header">
                  <Typography variant="h2" display="block" className='activity-block-title' mb={6}>
                    Recent Activity
                    {hasProPlan && (
                      <span onClick={()=>navigate('/audit')} className="activity-block-title-control">Feed</span>
                    )}
                  </Typography>
                  <div className='activity-block-title-sub'>
                    <Clock size={16} style={{marginRight: '8px'}}/>
                    Last 15 activities
                  </div>
                </div>

                <div className="activity-block-content">
                  {loadingActivity &&
                    <div className="spinner-wrapper">
                      <CircularProgress/>
                    </div>
                  }
                  {!loadingActivity && !activityRows && (
                    <div className="no-activity">Activity will be available soon.</div>
                  )}
                  {!loadingActivity && !!activityRows &&
                    Object.keys(activityRows).map((platformName: string) => {
                      const activity = activityRows[platformName];
                      return (
                        <div key={platformName} className="activity-block-platform">
                          <div className="activity-block-platform-title">
                            <div className="activity-block-platform-title-logo">
                              <img src={Platform[platformName]?.smallLogo} className="platformLogo" loading="lazy"/>
                              <div className="platform-name">
                                {Platform[platformName]?.title}
                                <span
                                  className="activity-block-platform-title-count"> {activity.length} activities</span>
                              </div>
                            </div>
                            <div
                              className={`activity-block-platform-title-control ${platformName}`}
                              onClick={() => {
                                const element = document.querySelector(`.activity-block-platform-content.${platformName}`);
                                const control = document.querySelector(`.activity-block-platform-title-control.${platformName}`);
                                if (element) {
                                  if (element.classList.contains('activity-block-platform-content-hidden')) {
                                    element.classList.remove('activity-block-platform-content-hidden')
                                    control.style.transform = 'rotate(180deg)'
                                  } else {
                                    element.classList.add('activity-block-platform-content-hidden')
                                    control.style.transform = 'rotate(0)'
                                  }
                                }
                              }}
                            >
                              <ArrowDown2 size={16}/>
                            </div>
                          </div>
                          <div
                            className={`activity-block-platform-content ${platformName} activity-block-platform-content-hidden`}
                            style={{height: `${activity.length * 48 + 32}px`}}
                          >
                            {activity.map((item, index) => {
                              // const isLast = index === activity.length - 1;
                              return (
                                <div key={index} className="activity-block-platform-content-item">
                                  {item.status === 'initialized' ?
                                    <Clock size="20" variant="Bold" color='#F59E0B'
                                           style={{margin: '2px 6px 0 0', minWidth: '20px'}}/> :
                                    <img src={item.status === 'success' ? iconSuccessGreen : errorIcon}
                                         loading="lazy" className='activity-block-platform-content-item-icon'/>
                                  }
                                  <div className="activity-block-platform-content-item-title">
                                    <div className="activity-block-platform-content-item-title-text">
                                      {capitalizeFirstLetterWithReplace(item.type || '')} - {item.name}
                                    </div>
                                    <span className="activity-block-platform-content-item-title-date">
                                    {new Date(item.date).toLocaleString()}
                                  </span>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      )
                    })}
                </div>
              </WhiteCard>
            </Grid>
          </Grid>
        </div>
      }
    </div>
  );
};

export default withRedirectAfterLogout()(DashboardPage);
